import React, { useState } from "react";
import { IconButton, useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";

import ConfirmationModal from "../modals/ConfirmationModal";
import { useDeleteBriefingMutation } from "../../store/apis/briefingApi";
import { setToastMessage } from "../../store/slices/app/appSlice";

interface DeleteBriefingButtonProps {
  briefingId: string;
  onDeleted?: () => void;
  size?: "small" | "medium";
}

const DeleteBriefingButton: React.FC<DeleteBriefingButtonProps> = ({
  briefingId,
  onDeleted,
  size = "small",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [deleteBriefing, { isLoading: isDeleting }] =
    useDeleteBriefingMutation();

  const handleClose = () => setOpen(false);

  const handleConfirm = async () => {
    try {
      await deleteBriefing({ id: briefingId }).unwrap();

      dispatch(
        setToastMessage({
          message: t(
            "briefings.deleteSuccess",
            "Briefing deleted successfully"
          ),
          severity: "success",
        })
      );
      onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message: t("briefings.deleteError", {
            defaultValue: "Unable to delete briefing",
            error: error instanceof Error ? error.message : "Unknown error",
          }),
          severity: "error",
        })
      );
    } finally {
      handleClose();
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        size={size}
        sx={{
          color: theme.palette.error.main,
          "&:hover": {
            backgroundColor: `${theme.palette.error.light}20`,
          },
        }}
      >
        <DeleteIcon />
      </IconButton>
      <ConfirmationModal
        open={open}
        title={t("briefings.deleteConfirmTitle", "Delete briefing")}
        message={t(
          "briefings.deleteConfirmMessage",
          "Are you sure you want to delete this briefing? This action cannot be undone."
        )}
        onConfirm={handleConfirm}
        onCancel={handleClose}
        isLoading={isDeleting}
      />
    </>
  );
};

export default DeleteBriefingButton;
